/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { FeatureCode, FeatureDetails, getDefaultScopePacks, ScopePackDataScope } from '@g17eco/core';
import type { RootConfig } from '../../types/initiative';

interface LimitCheck {
  current: number;
  limit: number;
}

export class FeaturePermissions {
  private static get(config: RootConfig | undefined, featureCode: FeatureCode): FeatureDetails | undefined {
    if (!config) {
      return;
    }
    return config.features?.find((f) => f.code === featureCode);
  }

  private static canAccess(config: RootConfig | undefined, featureCode: FeatureCode): boolean {
    return Boolean(FeaturePermissions.get(config, featureCode)?.active);
  }

  private static hasActiveFeature(config: RootConfig | undefined, featureCode: FeatureCode): boolean {
    return config?.features?.find((f) => f.code === featureCode)?.active ?? false;
  }

  private static getLimit(config: RootConfig | undefined, featureCode: FeatureCode): number | undefined {
    return FeaturePermissions.get(config, featureCode)?.config?.limit;
  }

  // Feature toggles
  public static canAccessAdminDashboard(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.AdminDashboard);
  }
  public static canAccessAssurance(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.Assurance);
  }
  public static canAccessBulkDelegation(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.BulkDelegation);
  }
  public static canAccessBulkImporting(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.BulkImporting);
  }

  public static canAccessCombinedReport(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.CombinedReport);
  }
  public static canAccessCombinedSurvey(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.CombinedSurvey);
  }
  public static canAccessSurveyTemplates(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.SurveyTemplates);
  }
  public static canAccessVerification(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.Verification);
  }
  public static canAccessDownloads(_config: RootConfig | undefined) {
    // We used to have this, so added a stub rather than remove the logic in the code in case we want to re-add
    return true;
  }

  public static canAccessCustomDashboards(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.CustomDashboards);
  }

  public static canSetNoteInstructions(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.NoteInstructions);
  }

  public static canAccessAIMetricAssistant(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.MetricAssistantAI);
  }

  public static canAccessAIDraftFurtherExplanation(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.DraftFurtherExplanationAI);
  }

  public static canAccessSDGInsightAI(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.SDGInsightAI);
  }

  public static canAccessAIEnhancedReports(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.AIEnhancedReports);
  }

  public static canAccessAIAutoAnswer(config: RootConfig | undefined) {
    return FeaturePermissions.hasActiveFeature(config, FeatureCode.AIAutoAnswer);
  }

  public static canAccessAIDocumentLibrary(config: RootConfig | undefined) {
    return FeaturePermissions.hasActiveFeature(config, FeatureCode.AIAccessDocumentLibrary);
  }

  public static canAccessAITextSummarization(config: RootConfig | undefined) {
    return FeaturePermissions.hasActiveFeature(config, FeatureCode.AITextSummarization);
  }

  public static canAccessScopePacks(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.ScopePacks);
  }

  public static canUseRichTextEditor(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.RichTextEditor);
  }

  public static canAccessAppIntegrations(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.AppIntegrations);
  }
  public static canAccessDataIntegrations(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.DataIntegrations);
  }
  public static canAccessInsightsIntelligence(config: RootConfig | undefined) {
    return FeaturePermissions.canAccess(config, FeatureCode.InsightsIntelligence);
  }
  public static canAccessAggregationOverrides(config: RootConfig | undefined) {
    return FeaturePermissions.hasActiveFeature(config, FeatureCode.AggregationOverrides);
  }

  // Feature limits
  public static getLimitCustomMetric(config: RootConfig | undefined) {
    return FeaturePermissions.getLimit(config, FeatureCode.CustomMetrics) ?? 0;
  }
  public static getLimitReportingLevels(config: RootConfig | undefined) {
    return FeaturePermissions.getLimit(config, FeatureCode.ReportingLevels) ?? 1;
  }
  public static getLimitUsers(config: RootConfig | undefined) {
    return FeaturePermissions.getLimit(config, FeatureCode.Users) ?? 1;
  }
  public static getLimitWorkgroups(config: RootConfig | undefined) {
    return FeaturePermissions.getLimit(config, FeatureCode.Workgroups) ?? 0;
  }
  public static getLimitSurveys(_config: RootConfig | undefined) {
    return -1; // must be below 0 to considered as No limit
  }

  public static isBelowLimit({ current, limit }: LimitCheck) {
    return limit < 0 || current < limit;
  }

  public static hasReachedLimit(check: LimitCheck) {
    return !FeaturePermissions.isBelowLimit(check);
  }

  /** Check what is supported in scope selection page **/
  public static getScopePackFeature(config: RootConfig | undefined): ScopePackDataScope {
    const scopePacks = FeaturePermissions.get(config, FeatureCode.ScopePacks);

    return scopePacks?.config?.scopePacks ?? getDefaultScopePacks();
  }

  public static getConfigLimit(config: RootConfig, featureCode: FeatureCode, defaultLimit: 0) {
    return config.features?.find((f) => f.code === featureCode)?.config?.limit ?? defaultLimit;
  }

  public static getAvailability(config: RootConfig | undefined, featureCode: FeatureCode) {
    const feature = FeaturePermissions.get(config, featureCode);
    return {
      active: feature?.active,
      disabled: feature?.disabled,
      disabledReason: feature?.disabledReason,
    };
  }
}
